import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import CustomButton from '../../components/CustomButton';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import SegmentedTabs, { SegmentedTabItem } from '../../components/SegmentedTabs';
import { setConfirmationDialogContent, setIsConfirmationDialog } from '../../store/slices/globalSlice';
import { tenantSelector } from '../../selector/authenticationSelector';
import { paths } from '../../routes';
import {
    useCreateOrganizationUnitMutation,
    useDeleteOrganizationMutation,
    useDeleteOrganizationUnitMutation,
    useGetCustomerClassesQuery,
    useGetOrganizationMembersQuery,
    useGetOrganizationQuery,
    useGetOrganizationSpaceAgreementsQuery,
    useUpdateOrganizationMutation,
    useUpdateOrganizationUnitMutation,
} from '../../api/organization/organizationApiSlice';
import {
    OrganizationBillingAddress,
    OrganizationDetail,
    OrganizationUnitWritePayload,
} from '../../api/organization/organizationApiTypes';
import { isValidBillingEmail, isValidCountryCode, isValidVatId } from '../../utils/organizationValidation';
import { leitwegIdProblem } from '../../utils/leitwegId';
import { useViewedTenantAccount } from '../../hooks/useViewedTenantAccount';
import {
    ORG_DETAIL_TAB_PARAM,
    OrgDetailTab,
    resolveOrgDetailTab,
    withOrgDetailTab,
} from './organizationDetailTabs';
import { shouldShowBillingSection } from './organizationSectionVisibility';
import { agreementsBlockingArchive } from './spaceAgreementStatus';
import OrgBasicInfoSection, { OrgBasicInfo } from './components/OrgBasicInfoSection';
import OrgBillingSection, { OrgBilling } from './components/OrgBillingSection';
import OrgClassSection from './components/OrgClassSection';
import OrgUnitsSection, { OrgUnitFormRow } from './components/OrgUnitsSection';
import OrgMembersSection from './components/OrgMembersSection';
import OrgRoomAccessSection from './components/OrgRoomAccessSection';
import OrgPublicPlanSection from './components/OrgPublicPlanSection';

interface OrgFormData {
    basic: OrgBasicInfo;
    customerClassId: string | null;
    billing: OrgBilling;
    units: OrgUnitFormRow[];
    /** D3b — may appear by name on the public plan. */
    showOnPublicPlan: boolean;
    /** The value the edit started from; the save sends the flag only when it differs. */
    loadedShowOnPublicPlan: boolean;
}

const buildInitialFormData = (org: OrganizationDetail): OrgFormData => ({
    basic: {
        legalName: org.legalName,
        displayName: org.displayName ?? '',
        type: org.type,
        status: org.status,
        // An "add a contact person" field on edit (#3037: several contacts
        // allowed). Never seeded — the existing contacts are starred in the
        // member list, and re-sending one of them would change nothing anyway.
        contactEmail: '',
        notes: org.notes ?? '',
    },
    customerClassId: org.customerClassId,
    billing: {
        street: org.billingAddress?.street ?? '',
        postalCode: org.billingAddress?.postalCode ?? '',
        city: org.billingAddress?.city ?? '',
        country: org.billingAddress?.country ?? '',
        vatId: org.vatId ?? '',
        billingEmail: org.billingEmail ?? '',
        invoiceContact: org.invoiceContact ?? '',
        defaultCostCenter: org.defaultCostCenter ?? '',
        leitwegId: org.leitwegId ?? '',
    },
    units: org.units.map((u) => ({
        id: u.id,
        name: u.name,
        customerClassId: u.customerClassId,
        defaultCostCenter: u.defaultCostCenter ?? '',
        leitwegId: u.leitwegId ?? '',
    })),
    showOnPublicPlan: org.showOnPublicPlan === true,
    loadedShowOnPublicPlan: org.showOnPublicPlan === true,
});

const trimOrNull = (value: string): string | null => {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
};

const isUnsavedRow = (id: string): boolean => id.startsWith('new-');

type Translate = (key: string) => string;

const firstValidationMessage = (
    t: Translate,
    legalNameMissing: boolean,
    typeMissing: boolean,
    billingEmailInvalid: boolean,
    vatIdInvalid: boolean,
    leitwegIdInvalid: boolean,
): string => {
    if (legalNameMissing) return t('organization.validation.legal_name_required');
    if (typeMissing) return t('organization.validation.type_required');
    if (billingEmailInvalid) return t('organization.validation.billing_email_invalid');
    if (vatIdInvalid) return t('organization.validation.vat_id_invalid');
    if (leitwegIdInvalid) return t('organization.validation.leitweg_id_invalid');
    return t('organization.validation.country_invalid');
};

const apiErrorMessage = (err: unknown, fallback: string): string => {
    const e = err as { data?: { message?: string }, message?: string };
    return e?.data?.message ?? e?.message ?? fallback;
};

const headerDisplayName = (
    mode: 'view' | 'edit',
    formData: OrgFormData | null,
    org: OrganizationDetail,
): string => {
    if (mode === 'edit' && formData) {
        return formData.basic.displayName.trim() || formData.basic.legalName.trim();
    }
    return org.displayName ?? org.legalName;
};

const toBillingAddressPayload = (billing: OrgBilling): OrganizationBillingAddress | null => {
    const street = billing.street.trim();
    const postalCode = billing.postalCode.trim();
    const city = billing.city.trim();
    const country = billing.country.trim();
    return (street || postalCode || city || country)
        ? { street, postalCode, city, country }
        : null;
};

interface UnitApi {
    createUnit: (args: { organizationId: string, body: OrganizationUnitWritePayload }) => Promise<unknown>;
    updateUnit: (args: { organizationId: string, unitId: string, body: OrganizationUnitWritePayload }) => Promise<unknown>;
    deleteUnit: (args: { organizationId: string, unitId: string }) => Promise<unknown>;
}

/** Diffs the form's unit rows against the persisted org and issues the deletes/creates/updates. */
const persistUnitRows = async (
    org: OrganizationDetail,
    organizationId: string,
    units: OrgUnitFormRow[],
    api: UnitApi,
): Promise<void> => {
    const formUnitIds = new Set(units.filter((u) => !isUnsavedRow(u.id)).map((u) => u.id));
    // Removed units: present on the backend but no longer in the form.
    const removed = org.units.filter((u) => !formUnitIds.has(u.id));
    for (const unit of removed) {
        await api.deleteUnit({ organizationId, unitId: unit.id });
    }
    for (const unit of units) {
        const name = unit.name.trim();
        if (name === '') continue;
        if (isUnsavedRow(unit.id)) {
            await api.createUnit({
                organizationId,
                body: {
                    name,
                    customerClassId: unit.customerClassId,
                    defaultCostCenter: trimOrNull(unit.defaultCostCenter),
                    leitwegId: trimOrNull(unit.leitwegId),
                },
            });
        } else {
            const original = org.units.find((u) => u.id === unit.id);
            const defaultCostCenter = trimOrNull(unit.defaultCostCenter);
            const leitwegId = trimOrNull(unit.leitwegId);
            const changed = !original
                || original.name !== name
                || original.customerClassId !== unit.customerClassId
                || (original.defaultCostCenter ?? null) !== defaultCostCenter
                || (original.leitwegId ?? null) !== leitwegId;
            if (changed) {
                await api.updateUnit({
                    organizationId,
                    unitId: unit.id,
                    body: { name, customerClassId: unit.customerClassId, defaultCostCenter, leitwegId },
                });
            }
        }
    }
};

/**
 * One customer organization, in three tabs (`?tab=details|members|rooms`).
 *
 * The split is on the INTERACTION model, not on how much content there is:
 * Details is one form with an edit/save cycle and a sticky Save bar, while
 * members mutate immediately through their own endpoints and never pass
 * through that bar. On one page the Save bar rendered *below* the member list,
 * so it drifted further from the fields it saves as membership grew — and the
 * list is the part that grows without bound.
 *
 * Rooms & agreements (#2144) is the third, and read-only in v1: its writes are
 * owner-gated and land with #2154. It follows the same rule — nothing on it
 * passes through the Details Save bar.
 *
 * `resolveOrgDetailTab` falls back to Details for an unknown value, so a link
 * to a tab that does not exist cannot render an empty page.
 */
const OrganizationDetailPage: React.FC = () => {
    const { organizationId } = useParams<{ organizationId: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const activeTab = resolveOrgDetailTab(searchParams.get(ORG_DETAIL_TAB_PARAM));

    const handleTabChange = useCallback(
        (key: OrgDetailTab) => {
            // `replace` keeps tab toggling out of the history stack, so Back
            // returns to the organizations list rather than walking the tabs.
            setSearchParams(withOrgDetailTab(searchParams, key), { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const [mode, setMode] = useState<'view' | 'edit'>('view');
    const [formData, setFormData] = useState<OrgFormData | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);

    // Derive the entity from the URL param (deep-link / refresh safe) rather
    // than assuming a preselected Redux entity (CLAUDE.md). Also skip until
    // the tenant is restored — on a hard reload the query would otherwise
    // race tenantCacheResetMiddleware's resetApiState and freeze in
    // isLoading (see OrganizationsTab).
    const tenant = useSelector(tenantSelector);
    const { data: org, isLoading, isError } = useGetOrganizationQuery(
        { id: organizationId ?? '' },
        { skip: !organizationId || !tenant },
    );
    const { data: customerClasses = [], isLoading: classesLoading } =
        useGetCustomerClassesQuery(undefined, { skip: !tenant });

    // An open edit belongs to the tenant it was started in (CLAUDE.md: reset
    // state on tenant switch). Guarded on the id, so first mount is a no-op.
    const previousTenantRef = useRef(tenant);
    useEffect(() => {
        if (previousTenantRef.current !== tenant) {
            previousTenantRef.current = tenant;
            setFormData(null);
            setSaveError(null);
            setMode('view');
        }
    }, [tenant]);

    // The member count belongs on the tab label — the reason for the tab is
    // that the list grows, and a tab that does not say how much is behind it
    // makes the operator click to find out. Same query and same cache entry
    // OrgMembersSection subscribes to, so this costs no extra request; the
    // page-level subscription also keeps the count while the Details tab is
    // open and the section is unmounted.
    const { data: members } = useGetOrganizationMembersQuery(
        { organizationId: organizationId ?? '' },
        { skip: !organizationId || !tenant },
    );

    // #2165 §4 — archive is refused while a space agreement is still running.
    // The API is the authority (ADR-0020, `archiveBlockingAgreements`); this
    // subscription exists so the operator learns the reason BEFORE clicking
    // rather than from a 409 afterwards. Same query and cache entry the Rooms &
    // agreements tab uses, so it costs no extra request once that tab has been
    // opened, and the default excludes archived agreements — which do not
    // block.
    const {
        data: agreementsData,
        isLoading: agreementsLoading,
        isError: agreementsError,
    } = useGetOrganizationSpaceAgreementsQuery(
        { organizationId: organizationId ?? '' },
        { skip: !organizationId || !tenant },
    );

    // Since #2154 there is no clock here at all: whether an agreement has ended
    // is the server's `hasEnded`, derived once per response against one clock,
    // so two agreements in the same list cannot disagree about it and the
    // client cannot disagree with the API that enforces the same rule. An
    // agreement lapsing while the page sits open is picked up by the refetch.
    const blockingAgreements = useMemo(
        () => agreementsBlockingArchive(agreementsData?.items ?? []),
        [agreementsData],
    );
    // Fail CLOSED on anything that is not a clean "no running agreements".
    // While the query is in flight, and if it failed, we do NOT know whether
    // archiving is allowed — and "not yet known" must never render as
    // "allowed". An errored query yields an empty list, which would otherwise
    // look exactly like "this organization has no agreements" and re-open the
    // very gap this guard closes (the error-state-vs-empty-state rule).
    const archiveBlocked = agreementsLoading || agreementsError || blockingAgreements.length > 0;

    // ADR-0012: `external` means the tenant's own system owns the invoice, so
    // kolula has nothing to do with a VAT id or an invoice address. An
    // organization that ALREADY carries billing data keeps the section anyway —
    // hiding it would not delete the values, it would make a wrong VAT id
    // impossible to see or correct.
    const { accountTenant } = useViewedTenantAccount();
    const showBilling = shouldShowBillingSection(
        accountTenant?.invoiceIssuer,
        org !== undefined && (
            org.vatId !== null || org.billingEmail !== null
            || org.invoiceContact !== null || org.billingAddress !== null
            || org.defaultCostCenter !== null || org.leitwegId !== null
        ),
    );

    const [updateOrganization, { isLoading: isSaving }] = useUpdateOrganizationMutation();
    const [deleteOrganization, { isLoading: isDeleting }] = useDeleteOrganizationMutation();
    const [createUnit] = useCreateOrganizationUnitMutation();
    const [updateUnit] = useUpdateOrganizationUnitMutation();
    const [deleteUnit] = useDeleteOrganizationUnitMutation();

    const handleEdit = useCallback(() => {
        if (!org) return;
        setFormData(buildInitialFormData(org));
        setSaveError(null);
        setMode('edit');
        // Every editable field lives on the Details tab. Entering edit mode
        // from the Members tab would otherwise swap the Edit button for a
        // Save bar with nothing editable on screen.
        handleTabChange('details');
    }, [org, handleTabChange]);

    const handleCancel = useCallback(() => {
        setFormData(null);
        setSaveError(null);
        setMode('view');
    }, []);

    const updateBasic = useCallback((updates: Partial<OrgBasicInfo>) => {
        setFormData((prev) => (prev ? { ...prev, basic: { ...prev.basic, ...updates } } : prev));
    }, []);

    const updateBilling = useCallback((updates: Partial<OrgBilling>) => {
        setFormData((prev) => (prev ? { ...prev, billing: { ...prev.billing, ...updates } } : prev));
    }, []);

    const updateUnits = useCallback((units: OrgUnitFormRow[]) => {
        setFormData((prev) => (prev ? { ...prev, units } : prev));
    }, []);

    const updateCustomerClass = useCallback((customerClassId: string | null) => {
        setFormData((prev) => (prev ? { ...prev, customerClassId } : prev));
    }, []);

    const updateShowOnPublicPlan = useCallback((showOnPublicPlan: boolean) => {
        setFormData((prev) => (prev ? { ...prev, showOnPublicPlan } : prev));
    }, []);

    // ─── Validation (derived from FORM STATE) ───────────────────────────────
    const legalNameMissing = (formData?.basic.legalName.trim() ?? '') === '';
    // Existing organizations always carry a type (the API has always required
    // one), so this can only be empty if an operator clears it in edit mode.
    const typeMissing = formData !== null && formData.basic.type === '';
    const billingEmailInvalid = useMemo(() => {
        const value = formData?.billing.billingEmail.trim() ?? '';
        return value !== '' && !isValidBillingEmail(value);
    }, [formData?.billing.billingEmail]);
    const vatIdInvalid = useMemo(() => {
        const value = formData?.billing.vatId.trim() ?? '';
        return value !== '' && !isValidVatId(value);
    }, [formData?.billing.vatId]);
    const countryInvalid = useMemo(() => {
        const value = formData?.billing.country.trim() ?? '';
        return value !== '' && !isValidCountryCode(value);
    }, [formData?.billing.country]);

    // The organisation's and every unit's: an office's own ID routes its
    // invoices just as the organisation's does.
    const orgLeitwegIdProblem = useMemo(
        () => leitwegIdProblem(formData?.billing.leitwegId ?? ''),
        [formData?.billing.leitwegId],
    );
    const leitwegIdInvalid = useMemo(
        () => orgLeitwegIdProblem !== null
            || (formData?.units ?? []).some((unit) => leitwegIdProblem(unit.leitwegId) !== null),
        [orgLeitwegIdProblem, formData?.units],
    );

    const formInvalid = legalNameMissing || typeMissing
        || billingEmailInvalid || vatIdInvalid || countryInvalid || leitwegIdInvalid;
    const validationError = formInvalid
        ? firstValidationMessage(t, legalNameMissing, typeMissing, billingEmailInvalid, vatIdInvalid, leitwegIdInvalid)
        : null;

    const persistUnitChanges = useCallback(
        async (data: OrgFormData) => {
            if (!org || !organizationId) return;
            await persistUnitRows(org, organizationId, data.units, {
                createUnit: (args) => createUnit(args).unwrap(),
                updateUnit: (args) => updateUnit(args).unwrap(),
                deleteUnit: (args) => deleteUnit(args).unwrap(),
            });
        },
        [org, organizationId, createUnit, updateUnit, deleteUnit],
    );

    const handleSave = useCallback(async () => {
        if (!formData || !organizationId) return;
        if (formInvalid) {
            setSaveError(
                firstValidationMessage(
                    t, legalNameMissing, typeMissing, billingEmailInvalid, vatIdInvalid, leitwegIdInvalid,
                ),
            );
            return;
        }
        // Narrowing, not a cast of convenience: `formInvalid` above returns
        // early while the type is unchosen, so it is a real one here.
        const type = formData.basic.type;
        if (type === '') return;
        setSaveError(null);

        try {
            await updateOrganization({
                id: organizationId,
                body: {
                    legalName: formData.basic.legalName.trim(),
                    displayName: trimOrNull(formData.basic.displayName),
                    type,
                    customerClassId: formData.customerClassId,
                    billingAddress: toBillingAddressPayload(formData.billing),
                    vatId: trimOrNull(formData.billing.vatId),
                    billingEmail: trimOrNull(formData.billing.billingEmail),
                    invoiceContact: trimOrNull(formData.billing.invoiceContact),
                    defaultCostCenter: trimOrNull(formData.billing.defaultCostCenter),
                    leitwegId: trimOrNull(formData.billing.leitwegId),
                    status: formData.basic.status,
                    // D3b. Sent only when the operator changed it in this edit:
                    // omitted keeps the stored value, so saving other fields
                    // from a form loaded before someone else ticked the box
                    // cannot untick it.
                    ...(formData.showOnPublicPlan === formData.loadedShowOnPublicPlan
                        ? {}
                        : { showOnPublicPlan: formData.showOnPublicPlan }),
                    // #3037. Sent only when filled: it ADDS a contact. An empty
                    // field must not be sent as null, which would clear every
                    // contact of the organization.
                    ...(trimOrNull(formData.basic.contactEmail)
                        ? { contactEmail: trimOrNull(formData.basic.contactEmail) }
                        : {}),
                    notes: trimOrNull(formData.basic.notes),
                },
            }).unwrap();
            await persistUnitChanges(formData);
            setFormData(null);
            setMode('view');
        } catch (err: unknown) {
            setSaveError(apiErrorMessage(err, t('organization.detail.save_error')));
        }
    }, [
        formData, organizationId, formInvalid, legalNameMissing, typeMissing,
        billingEmailInvalid, vatIdInvalid, leitwegIdInvalid,
        updateOrganization, persistUnitChanges, t,
    ]);

    const handleConfirmArchive = useCallback(async () => {
        if (!organizationId) return;
        try {
            await deleteOrganization({ id: organizationId }).unwrap();
            dispatch(setIsConfirmationDialog(false));
            navigate(paths.customers);
        } catch (err: unknown) {
            dispatch(setIsConfirmationDialog(false));
            setSaveError(apiErrorMessage(err, t('organization.detail.archive_error')));
        }
    }, [organizationId, deleteOrganization, dispatch, navigate, t]);

    const handleClickArchive = useCallback(() => {
        dispatch(setIsConfirmationDialog(true));
        dispatch(
            setConfirmationDialogContent({
                content: t('organization.detail.confirm_archive.content'),
                headerText: t('organization.detail.confirm_archive.header'),
                leftButtonText: t('organization.detail.confirm_archive.cancel'),
                rightButtonText: t('organization.detail.confirm_archive.confirm'),
            }),
        );
    }, [dispatch, t]);

    if (isLoading || !tenant) {
        return (
            <div className={'p-6 flex items-center justify-center min-h-[400px]'}>
                <div className={'animate-spin rounded-full h-12 w-12 border-b-2 border-primary'} />
            </div>
        );
    }

    if (isError || !org) {
        return (
            <div className={'p-6 flex flex-col gap-4'}>
                <PageHeader>
                    <PageHeadline>{t('organization.detail.not_found.title')}</PageHeadline>
                </PageHeader>
                <div className={'bg-text-contrast rounded-[5px] shadow-sm p-6 flex flex-col items-center gap-4'}>
                    <p className={'text-foreground/60'}>{t('organization.detail.not_found.message')}</p>
                    <button
                        onClick={() => navigate(paths.customers)}
                        className={'text-primary hover:underline'}
                    >
                        {t('organization.detail.not_found.back_to_overview')}
                    </button>
                </div>
            </div>
        );
    }

    const displayName = headerDisplayName(mode, formData, org);

    const isEditing = mode === 'edit' && formData !== null;

    // In view mode the sections render the persisted org read-only via the same
    // editable inputs (disabled); edit mode renders the live form state. The
    // update* handlers are safe to keep wired in view mode: formData is null
    // there, so their setFormData guard makes them no-ops.
    const sectionData = formData ?? buildInitialFormData(org);

    const tabs: SegmentedTabItem<OrgDetailTab>[] = [
        { key: 'details', label: t('organization.tabs.details') },
        {
            key: 'members',
            // Until the list has loaded the label carries no count rather than
            // a placeholder "0" — an org with members would briefly claim to
            // have none.
            //
            // `total`, not `count`: `count` is i18next's pluralization key, so
            // it would send the lookup to `members_with_count_one` /
            // `_other` — keys we do not define — instead of interpolating.
            label: members
                ? t('organization.tabs.members_with_count', { total: members.length })
                : t('organization.tabs.members'),
        },
        // #2144. Read-only in v1: the writes are owner-gated and land with
        // #2154, but seeing what is contracted should not wait for them.
        { key: 'rooms', label: t('organization.tabs.rooms') },
    ];

    return (
        <div className={'p-6 flex flex-col gap-6'}>
            <div className={'sticky top-16 z-30 bg-background -mx-6 px-6 -mt-6 pt-6 pb-3'}>
                <PageHeader>
                    <div className={'flex-grow'}>
                        <PageHeadline>{t('organization.detail.title')}</PageHeadline>
                        <p className={'text-sm text-foreground/60'}>{displayName}</p>
                    </div>
                    {mode === 'view' && (
                        <div className={'flex items-center gap-2'}>
                            {/* #2165 §4 — archive only where there is no
                                contract to end: a duplicate, a typo, a prospect
                                that never signed. A live customer is CANCELLED,
                                with an end date, not archived on the spot. */}
                            {org.archivedAt === null && (
                                <CustomButton
                                    title={t('organization.detail.actions.archive')}
                                    onClick={handleClickArchive}
                                    isFilled={false}
                                    variant={'delete'}
                                    disabled={archiveBlocked}
                                />
                            )}
                            <CustomButton
                                title={t('organization.detail.actions.edit')}
                                onClick={handleEdit}
                                isFilled={true}
                            />
                        </div>
                    )}
                </PageHeader>
            </div>

            {saveError && (
                <div className={'bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800'}>
                    {saveError}
                </div>
            )}

            {org.archivedAt !== null && (
                <div className={'flex items-center gap-2 -mt-2'}>
                    <span className={'px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'}>
                        {t('organization.detail.status.archived')}
                    </span>
                </div>
            )}

            {/* #2165 §4 — a disabled button with no reason reads as a broken
                screen, so the refusal names itself. Only once the agreements
                are actually known: while they load the button is disabled but
                nothing is claimed, because "not yet known" is not "blocked". */}
            {org.archivedAt === null && blockingAgreements.length > 0 && mode === 'view' && (
                <p className={'-mt-2 text-sm text-foreground/60'}>
                    {t('organization.detail.archive_blocked', {
                        total: blockingAgreements.length,
                    })}
                </p>
            )}

            <SegmentedTabs items={tabs} value={activeTab} onChange={handleTabChange} />

            {activeTab === 'details' && (
                <>
                    {isEditing && (
                        <p className={'text-sm text-foreground/60'}>{t('common.required_fields')}</p>
                    )}

                    <OrgBasicInfoSection
                        value={sectionData.basic}
                        onChange={updateBasic}
                        legalNameError={isEditing && legalNameMissing}
                        typeError={isEditing && typeMissing}
                        disabled={!isEditing}
                        contactFieldMode={'add'}
                    />

                    <OrgClassSection
                        value={sectionData.customerClassId}
                        onChange={updateCustomerClass}
                        classes={customerClasses}
                        isLoading={classesLoading}
                        disabled={!isEditing}
                    />

                    {showBilling && (
                        <OrgBillingSection
                            value={sectionData.billing}
                            onChange={updateBilling}
                            billingEmailError={isEditing && billingEmailInvalid}
                            vatIdError={isEditing && vatIdInvalid}
                            countryError={isEditing && countryInvalid}
                            leitwegIdError={isEditing ? orgLeitwegIdProblem : null}
                            disabled={!isEditing}
                        />
                    )}

                    <OrgUnitsSection
                        units={sectionData.units}
                        onChange={updateUnits}
                        classes={customerClasses}
                        isLoading={classesLoading}
                        disabled={!isEditing}
                    />

                    <OrgPublicPlanSection
                        value={sectionData.showOnPublicPlan}
                        onChange={updateShowOnPublicPlan}
                        disabled={!isEditing}
                    />
                </>
            )}

            {/* Members mutate immediately via dedicated endpoints — deliberately
                outside the form's edit/save cycle (plan §4.3a), which is why
                they get their own tab rather than sitting under the Save bar. */}
            {activeTab === 'members' && organizationId && (
                <OrgMembersSection organizationId={organizationId} units={org.units} />
            )}

            {activeTab === 'rooms' && organizationId && (
                <OrgRoomAccessSection organizationId={organizationId} />
            )}

            {/* The Save bar stays mounted on BOTH tabs while editing. Hiding it
                on the Members tab would leave unsaved edits with no visible way
                to commit or discard them — and nothing on screen saying they
                exist. `formData` is this component's state and the tabs only
                switch what renders, so the edits themselves survive the
                switch. */}
            {isEditing && (
                <div className={'sticky bottom-0 bg-text-contrast border-t border-foreground/10 px-6 py-3 '
                    + 'flex items-center justify-end gap-3 shadow-lg z-10 -mx-6 -mb-6'}
                >
                    {/* Same inline reason as the create page: the save button is
                        disabled while the form is invalid, so the footer states
                        why instead of leaving a dead button unexplained. On the
                        Members tab it names what the bar refers to instead —
                        members save on their own and never through it. */}
                    {activeTab === 'members' ? (
                        <p className={'mr-auto text-sm text-foreground/60'}>
                            {t('organization.detail.unsaved_details')}
                        </p>
                    ) : validationError && (
                        <p className={'mr-auto text-sm text-foreground/60'}>{validationError}</p>
                    )}
                    <CustomButton
                        title={t('common.cancel')}
                        onClick={handleCancel}
                        isFilled={false}
                        disabled={isSaving}
                    />
                    <CustomButton
                        title={t('common.save')}
                        onClick={handleSave}
                        isFilled={true}
                        disabled={isSaving || formInvalid}
                        loading={isSaving}
                    />
                </div>
            )}

            <ConfirmationDialog
                handleClickConfirm={handleConfirmArchive}
                isLoading={isDeleting}
            />
        </div>
    );
};

export default OrganizationDetailPage;
